/**
 * Scores at a glance: a one-line run summary (items, cost, latency), then the
 * radar beside one card per evaluator, led by the overall mean. While running,
 * the cards use `live` — means over the rows scored so far — and say so.
 */

import { Link } from "react-router";
import { Box, SectionLabel } from "@/components/ui/box";
import { kindLabel } from "@/features/evaluators/labels";
import { formatCount, formatPercent, formatScore, plural } from "@/lib/format";
import { ExperimentStats } from "./experiment-stats";
import { ScoreRadar } from "./radar-chart";
import { RemappedBadge } from "./remapped-badge";
import { checksPerEvaluator } from "../scoring/scoring-tally";
import { isLive } from "../status";
import type { ExperimentEvaluator, ExperimentSummary } from "../types";

function scoreFor(e: ExperimentSummary, ev: ExperimentEvaluator): number | null {
  if (isLive(e.status)) return e.live?.[ev.key]?.mean ?? null;
  return e.aggregate?.evaluators[ev.key]?.mean ?? null;
}

/** What happened to checks this evaluator never ran, so the card adds up to the Checks line:
 *  a failed run counts them as failed, any other ended run as not scored. */
function problems(e: ExperimentSummary, errors: number, unrun: number): string | false {
  if (unrun <= 0) return errors > 0 && plural(errors, "error");
  if (e.status !== "failed")
    return [errors > 0 && plural(errors, "error"), `${formatCount(unrun)} not scored`]
      .filter(Boolean)
      .join(" · ");
  const why = [errors > 0 && plural(errors, "error"), `${formatCount(unrun)} never ran`].filter(Boolean);
  return `${formatCount(errors + unrun)} failed: ${why.join(", ")}`;
}

/** "pass 92% · n 117", then "2 not applicable · 1 error" when there are any. */
function resultLines(e: ExperimentSummary, ev: ExperimentEvaluator): string[] {
  if (isLive(e.status)) return [`So far · n ${e.live?.[ev.key]?.n ?? 0}`];
  const agg = e.aggregate?.evaluators[ev.key];
  if (!agg) return [];
  const owed = checksPerEvaluator(e);
  const unrun = owed === null ? 0 : owed - agg.n - agg.not_applicable - agg.errors;
  const extra = [
    agg.not_applicable > 0 && `${agg.not_applicable} not applicable`,
    problems(e, agg.errors, unrun),
  ].filter(Boolean);
  return [`pass ${formatPercent(agg.pass_rate)} · n ${agg.n}`, ...(extra.length ? [extra.join(" · ")] : [])];
}

function ScoreCard({
  e,
  ev,
  value,
}: {
  e: ExperimentSummary;
  ev: ExperimentEvaluator;
  value: number | null;
}) {
  const kind = `${ev.version !== undefined ? `v${ev.version} · ` : ""}${kindLabel(ev.kind)} · ${ev.is_paid ? "paid" : "free"}`;
  return (
    <div className="flex min-w-0 flex-col gap-1 rounded-[9px] border border-line bg-surface px-3.5 py-3">
      <Link
        to={`/evaluators/${ev.id}`}
        className="truncate text-xs font-semibold text-muted hover:text-ink hover:underline"
        title={`Open ${ev.name}`}
      >
        {ev.name}
      </Link>
      <span className="font-mono text-2xl leading-tight font-medium tracking-[-0.02em]">
        {formatScore(value)}
      </span>
      {resultLines(e, ev).map((line) => (
        <span key={line} className="text-[11px] text-muted">
          {line}
        </span>
      ))}
      <span className="flex flex-wrap items-center gap-1.5 pt-0.5">
        <span className="text-[11px] text-faint">{kind}</span>
        <RemappedBadge version={ev.version} remapped={ev.remapped} />
      </span>
    </div>
  );
}

export function ExperimentScores({ experiment: e }: { experiment: ExperimentSummary }) {
  const live = isLive(e.status);
  const values = e.evaluators.map((ev) => ({ ev, value: scoreFor(e, ev) }));
  const scored = values.filter((v): v is { ev: ExperimentEvaluator; value: number } => v.value !== null);
  const overall = live
    ? scored.length
      ? scored.reduce((a, v) => a + v.value, 0) / scored.length
      : null
    : (e.aggregate?.overall ?? null);
  const radar = scored.map((v) => ({
    name: v.ev.name,
    value: v.value,
    pass: live ? null : e.aggregate?.evaluators[v.ev.key]?.pass_rate,
  }));

  return (
    <section aria-label="Scores" className="flex flex-col gap-3">
      <ExperimentStats experiment={e} />
      <div className="grid gap-3 lg:grid-cols-[minmax(320px,400px)_minmax(0,1fr)]">
        <Box className="flex flex-col gap-1 px-3 py-3">
          <div className="px-1">
            <SectionLabel>By evaluator</SectionLabel>
          </div>
          <ScoreRadar data={radar} />
        </Box>
        <div className="grid content-start gap-2.5 [grid-template-columns:repeat(auto-fill,minmax(170px,1fr))]">
          <div className="flex flex-col gap-1 rounded-[9px] border border-subtle bg-subtle px-3.5 py-3">
            <span className="text-xs font-semibold text-ink">Overall</span>
            <span className="font-mono text-[28px] leading-tight font-semibold tracking-[-0.02em]">
              {formatScore(overall)}
            </span>
            <span className="text-[11px] text-muted">
              {live ? "So far · " : ""}mean of {plural(e.evaluators.length, "evaluator")}
            </span>
          </div>
          {values.map(({ ev, value }) => (
            <ScoreCard key={ev.id} e={e} ev={ev} value={value} />
          ))}
        </div>
      </div>
    </section>
  );
}
